import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, IntroScreenHeader, SectionDescription
} from '../../components';
import { useScrollToTop } from '../../hooks';

const sections = [
  {
    title: 'What is a Durable Power of Attorney for Finances?',
    description: 'A legal document that allows you to appoint someone to make financial decisions on your behalf if you become unable to do so yourself.'
  },
  {
    title: 'When to Make a Power of Attorney',
    description: 'Learn about the best times to create this important document and why it\'s crucial for your financial security.'
  },
  {
    title: 'When Your Document Takes Effect',
    description: 'Understand the different ways your Power of Attorney can become effective and choose what works best for you.'
  },
  {
    title: 'Personal Information',
    description: 'Provide your basic information including name, address, and contact details.'
  },
  {
    title: 'Choosing Your Agent',
    description: 'Learn how to select the right person to act as your financial agent.'
  },
  {
    title: 'Naming Multiple Agents',
    description: 'Understand your options for appointing one or more agents to handle your finances.'
  },
  {
    title: 'Agent Details',
    description: 'Provide complete contact information for your chosen agent(s).'
  },
  {
    title: 'Assigning Powers',
    description: 'Specify exactly what your agent can and cannot do with your finances.'
  },
  {
    title: 'Gift Authorization',
    description: 'Decide whether your agent can make gifts on your behalf and to whom.'
  },
  {
    title: 'Periodic Reports',
    description: 'Choose whether to require regular reports from your agent about their actions.'
  },
  {
    title: 'Finalizing Your Document',
    description: 'Complete the legal requirements for your state including witnesses and notarization.'
  }
];

export function NewPOAIntroScreen() {
  const navigate = useNavigate();
  
  useScrollToTop();
  
  const handleCancel = useCallback(() => {
    navigate('/home');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    navigate('../when-to-make');
  }, [ navigate ]);
  
  return (
    <div className="new-poa-intro-screen screen-container screen">
      <IntroScreenHeader
        title="Durable Power of Attorney for Finances"
        description="This guided process will help you create a comprehensive Power of Attorney document that protects your financial interests and gives you peace of mind."
      />
      
      <div className="definition-section">
        <h3>What is a Durable Power of Attorney for Finances?</h3>
        <p>
          A Durable Power of Attorney for Finances is a legal document that allows you to appoint 
          someone (called an "agent" or "attorney-in-fact") to make financial decisions on your 
          behalf if you become unable to do so yourself due to illness, injury, or other circumstances.
        </p>
        <p>
          Unlike a regular Power of Attorney, a "durable" one remains in effect even if you become 
          incapacitated, making it an essential part of comprehensive estate planning.
        </p>
      </div>

      <div className="process-overview">
        <h3>What We'll Cover</h3>
        {sections.map((section, index) => (
          <SectionDescription 
            key={index} 
            title={section.title} 
            description={section.description} 
          />
        ))}
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        handleSecondary={handleCancel}
        primaryText="Get Started"
        secondaryText="Cancel"
      />
    </div>
  );
}
